// @flow
import React, { Fragment, } from 'react';
import type { ChildrenArray, Element, Node, } from 'react';
import { FelaComponent, FelaTheme, } from 'react-fela';
import {
  AriaLive,
  DeviceTypeInjector,
  LayoutContainer,
  AdSlot,
  Grid,
  GridItem,
  IconFinanceLogo,
  Masthead,
  Footer,
  GetComponentProvider,
  useSlotData,
  WebViewExclude,
  SortableTable,
} from '@haaretz/htz-components';

import NavigationBar from '../components/NavigationBar/NavigationBar';
import RowItem from '../components/RowItem/RowItem';
import PageRow from '../components/PageRow/PageRow';
import SearchBar from '../components/SearchBar/SearchBar';
import getElements from '../utils/getPageElementsBS';
import MobileSearch from '../components/MobileSearch/MobileSearch';
import FinanceDataGetter from './FinanceDataGetter';
import useAllAdUnits from '../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../components/AdManager/useAdUnitPathTemplate';

const banners = [ 'native1', 'native2', 'native3', 'native4', ];

const numToString = num => (typeof num === 'number'
  ? num.toLocaleString('he', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  : num);

// Shared field configurations to reduce duplication
const createChangePercentageField = theme => ({
  name: 'changePercentage',
  display: '% שינוי',
  sortingOrder: 'desc',
  style: ({ changePercentage, }) => ({
    ...(changePercentage ? { color: changePercentage < 0 ? theme.color('negative') : theme.color('positive'), } : {}),
    direction: 'ltr',
    fontWeight: '700',
    paddingEnd: '2rem',
    position: 'relative',
    textAlign: 'start',
  }),
  value: ({ changePercentage, }) => (changePercentage ? `${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}${numToString(Math.abs(changePercentage))}%` : '-'),
});

const NAME_FIELD_STYLES = {
  default: () => ({ fontWeight: '700', maxWidth: '17rem', paddingStart: '2rem', }),
  ellipsis: () => ({ fontWeight: '700', paddingStart: '2rem', textOverflow: 'ellipsis', whiteSpace: 'nowrap', }),
  minimal: () => ({ fontWeight: '700', paddingStart: '2rem', }),
};

const createNameField = (display, styleType = 'default') => ({
  name: 'name',
  display,
  sortingOrder: 'asc',
  style: NAME_FIELD_STYLES[styleType],
  headerStyle: { width: '45%', },
  value: ({ name, }) => name,
});

const createStandardFields = (nameDisplay, styleType = 'default') => theme => [
  createNameField(nameDisplay, styleType),
  createChangePercentageField(theme),
];

// Configuration for dynamic section based on page type
const getSectionConfig = (section: ?string) => {
  const configs = {
    stock: {
      title: 'מניות ארביטראז׳',
      queryPrefix: 'Arbitrage',
      filters: [ { section: 'index', subSection: '-2000', }, ],
      type: 'stock',
      fragment: 'dualName symbol arbGap dualID',
      link: { href: '/arbitrage', as: '/arbitrage', text: 'לרשימה המלאה', },
      fields: theme => [
        {
          name: 'dualName',
          display: 'שם נייר',
          sortingOrder: 'asc',
          style: NAME_FIELD_STYLES.default,
          headerStyle: { width: '45%', },
          value: ({ dualName, }) => dualName,
          path: { type: 'type', id: 'dualID', },
        },
        {
          name: 'arbGap',
          display: "פער ארביטראז'",
          sortingOrder: 'desc',
          style: ({ arbGap, }) => ({
            ...(arbGap ? { color: arbGap < 0 ? theme.color('negative') : theme.color('positive'), } : {}),
            direction: 'ltr',
            fontWeight: '700',
            paddingEnd: '2rem',
            position: 'relative',
            textAlign: 'start',
          }),
          value: ({ arbGap, }) => `${arbGap > 0 ? '' : arbGap < 0 ? '-' : ''}${arbGap ? `${numToString(Math.abs(arbGap))}%` : '-'}`,
        },
      ],
      initialSort: 'arbGap',
    },
    arbitrage: {
      title: 'מניות תל אביב',
      queryPrefix: 'TA',
      filters: [ { section: 'index', subSection: '2', }, ],
      type: 'stock',
      fragment: 'name value changePercentage',
      link: { href: '/stock', as: '/stock', text: 'לבורסת תל אביב', },
      fields: createStandardFields('שם נייר', 'ellipsis'),
      initialSort: 'changePercentage',
    },
    crypto: {
      title: 'מטבעות דיגיטליים',
      queryPrefix: 'Crypto',
      filters: [ { section: 'type', subSection: 'crypto', }, ],
      fragment: 'name value changePercentage',
      link: { href: '/crypto', as: '/crypto', text: 'למדור מטבעות דיגיטליים', },
      fields: createStandardFields('שם מטבע'),
      initialSort: 'changePercentage',
    },
    etf: {
      title: 'קרנות סל',
      queryPrefix: 'Etf',
      filters: [ { section: 'type', subSection: 'etf', }, ],
      fragment: 'name value changePercentage',
      link: { href: '/etf', as: '/etf', text: 'לקרנות סל', },
      fields: createStandardFields('שם נייר'),
      initialSort: 'changePercentage',
    },
    exchange: {
      title: 'מט״ח',
      queryPrefix: 'Exchange',
      filters: [ { section: 'type', subSection: 'currency', }, ],
      fragment: 'name value changePercentage',
      link: { href: '/exchange', as: '/exchange', text: 'למדור מט״ח', },
      fields: createStandardFields('שם מטבע', 'minimal'),
      initialSort: 'changePercentage',
    },
    bond: {
      title: 'אג״ח',
      queryPrefix: 'Bond',
      filters: [ { section: 'type', subSection: 'bond', }, ],
      fragment: 'name value changePercentage',
      link: { href: '/bond', as: '/bond', text: 'לאג״ח', },
      fields: createStandardFields('שם נייר'),
      initialSort: 'changePercentage',
    },
    indices: {
      title: 'מדדים',
      queryPrefix: 'Indices',
      filters: [ { section: 'type', subSection: 'index', }, ],
      fragment: 'name value changePercentage',
      link: { href: '/indices', as: '/indices', text: 'למדדים', },
      fields: createStandardFields('שם מדד'),
      initialSort: 'changePercentage',
    },
    options: {
      title: 'מניות תל אביב',
      queryPrefix: 'TAOptions',
      filters: [ { section: 'index', subSection: '2', }, ],
      type: 'stock',
      fragment: 'name value changePercentage',
      link: { href: '/stock', as: '/stock', text: 'לבורסת תל אביב', },
      fields: createStandardFields('שם נייר', 'ellipsis'),
      initialSort: 'changePercentage',
    },
  };

  // Default to קרנות נאמנות for homepage and mtf sections
  const defaultConfig = {
    title: 'קרנות נאמנות',
    queryPrefix: 'Mtf',
    filters: [ { section: 'type', subSection: 'mtf', }, ],
    fragment: 'name value changePercentage',
    link: { href: '/mtf', as: '/mtf', text: 'לקרנות נאמנות', },
    fields: createStandardFields('שם נייר'),
    initialSort: 'changePercentage',
  };

  return configs[section] || defaultConfig;
};

type Props = {
  children: ChildrenArray<Element<any>>,
  section: ?string,
};

// TODO: Move all elements to CMS
// const menuItems = {
//   menu: {
//     promotions: [
//       {
//         name: 'רכשו מינוי',
//         url: 'https://promotions.themarker.com/promotions-page/product',
//       },
//     ],
//     sites: [
//       {
//         name: 'Haaretz Com',
//         url: 'http://www.haaretz.com/',
//       },
//       {
//         name: 'TheMarker',
//         url: 'http://www.themarker.com/',
//       },
//       {
//         name: 'הארץ',
//         url: 'http://www.haaretz.co.il/',
//       },
//     ],
//     items: [
//       {
//         name: 'ראשי',
//         url: '/',
//       },
//       {
//         name: 'מניות',
//         url: '/stock',
//       },
//       {
//         name: 'ארביטראז\'',
//         url: '/arbitrage',
//       },
//       {
//         name: 'קרנות נאמנות',
//         url: '/mtf',
//       },
//       {
//         name: 'קרנות סל',
//         url: '/etf',
//       },
//       {
//         name: 'אג"ח',
//         url: '/bond',
//       },
//       {
//         name: 'אופציות',
//         url: '/options',
//       },
//       {
//         name: 'מט"ח',
//         url: '/exchange',
//       },
//       {
//         name: 'מטבעות דיגיטליים',
//         url: '/crypto',
//       },
//     ],
//   },
// };

MainLayout.defaultProps = {
  section: null,
};

function MainLayout({
  children,
  section,
}: Props): Node {
  const headerData = useSlotData('header');
  const header = headerData || [];
  const pageNavigation = header.find(item => item.inputTemplate === 'PageNavigation');

  const adUnits = useAllAdUnits();
  const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <FelaTheme>
      {theme => (
        <GetComponentProvider value={getElements}>
          <Fragment>
            <AriaLive />
            <DeviceTypeInjector />
            <WebViewExclude>
              <Masthead
                pageType={section === 'index' ? 'homepage' : 'finance'}
                searchComponent={{
                  mobile: MobileSearch,
                  desktop: SearchBar,
                }}
                hideSearch
                menuItems={pageNavigation?.menuItems || []}
                rowBgc={[
                  { until: 's', value: 'transparent', },
                  { from: 's', value: [ 'neutral', '-6', ], },
                ]}
                wrapperMiscStyles={{
                  backgroundColor: [ { until: 's', value: theme.color('white'), }, ],
                  backgroundImage: 'none',
                  borderBottom: [ '1px', 1, 'solid', theme.color('masthead', 'border'), ],
                }}
              />
            </WebViewExclude>
            <FelaComponent
              style={{
                backgroundColor: theme.color('neutral', '-6'),
                ...theme.mq({ until: 's', }, { paddingTop: '3rem', }),
              }}
            >
              <WebViewExclude>
                <LayoutContainer
                  bgc="transparent"
                  miscStyles={{
                    paddingStart: [ { until: 's', value: '2rem', }, ],
                    paddingEnd: [ { until: 's', value: '2rem', }, ],
                  }}
                >
                  <NavigationBar
                    section={section}
                    miscStyles={{
                      display: [ { until: 'l', value: 'none', }, ],
                    }}
                  />
                  <FelaComponent
                    style={{
                      marginBottom: '1rem',
                      ...theme.mq({ from: 's', }, { display: 'none', }),
                    }}
                  >
                    <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.interstitial'], adUnitPathTemplate)} />
                  </FelaComponent>
                  <FelaComponent
                    style={{
                      marginBottom: '2rem',
                      paddingTop: '1px',
                    }}
                  >
                    <AdSlot
                      {...createAdSlotProps(adUnits['finance.themarker.com.billboard'], adUnitPathTemplate)}
                      wrapperMiscStyles={{
                        ':not(:empty)': {
                          paddingTop: 'calc(4rem - 1px)',
                        },
                      }}
                    />
                  </FelaComponent>
                </LayoutContainer>
              </WebViewExclude>
              <LayoutContainer
                tagName="main"
                bgc="transparent"
                miscStyles={{
                  display: [ { from: 'l', value: 'flex', }, ],
                  paddingStart: [
                    { until: 'l', value: '2rem', },
                    { from: 'l', until: 'xl', value: '11rem', },
                  ],
                  paddingEnd: [
                    { until: 'l', value: '2rem', },
                    { from: 'l', until: 'xl', value: '11rem', },
                  ],
                }}
              >
                <FelaComponent
                  style={{
                    width: '100%',
                  }}
                >
                  {children}
                  {/* Row 3: Dynamic section (1/3) + Labels (2/3) from L */}
                  <PageRow miscStyles={{ marginBottom: [ { from: 'l', value: '4rem', }, ], }}>
                    <Grid gutter={4}>
                      <GridItem
                        width={[
                          { from: 'xl', value: 1 / 3, },
                        ]}
                        miscStyles={{
                          display: [ { until: 'xl', value: 'none', }, { from: 'xl', value: 'block', }, ],
                        }}
                      >
                        <FelaTheme>
                          {themeInner => {
                            const sectionConfig = getSectionConfig(section);
                            return (
                              <RowItem title={sectionConfig.title}>
                                <SortableTable
                                  queryPrefix={sectionConfig.queryPrefix}
                                  filters={sectionConfig.filters}
                                  type={sectionConfig.type}
                                  fragment={sectionConfig.fragment}
                                  link={sectionConfig.link}
                                  fields={sectionConfig.fields(themeInner)}
                                  initialSort={sectionConfig.initialSort}
                                  count={5}
                                />
                              </RowItem>
                            );
                          }}
                        </FelaTheme>
                      </GridItem>
                      <GridItem
                        width={[
                          { until: 'xl', value: 1, },
                          { from: 'xl', value: 2 / 3, },
                        ]}
                      >
                        <RowItem
                          title="Labels"
                          isCommercial
                        >
                          <Grid
                            gutter={2}
                            miscStyles={{
                              marginTop: '2rem',
                              justifyContent: [ { until: 's', value: 'center', }, ],
                            }}
                          >
                            {
                              banners.map(banner => (
                                <GridItem
                                  key={`banner-${banner}`}
                                  width={[
                                    { until: 's', value: 5 / 6, },
                                    { from: 's', until: 'l', value: 1 / 2, },
                                    { from: 'l', value: 1 / banners.length, },
                                  ]}
                                >
                                  <AdSlot {...createAdSlotProps(adUnits[`finance.themarker.com.${banner}`], adUnitPathTemplate)} />
                                </GridItem>
                              ))
                            }
                          </Grid>
                        </RowItem>
                      </GridItem>
                    </Grid>
                  </PageRow>
                </FelaComponent>
              </LayoutContainer>
            </FelaComponent>
            <WebViewExclude>
              <Footer
                contentId="TheMarker.Element.FooterElement"
                logo={({ size, miscStyles, }) => (
                  <IconFinanceLogo size={size} color="primary" miscStyles={miscStyles} />
                )}
                logoAltText="פייננס"
              />
            </WebViewExclude>
            <FelaComponent
              style={{
                extend: [
                  theme.mq({ until: 's', }, {
                    marginBottom: [
                      theme.mobileStyle.menuHeight,
                      `calc(${theme.mobileStyle.menuHeight} + env(safe-area-inset-bottom, 0))`,
                    ],
                  }),
                  theme.mq({ from: 'l', }, {
                    display: 'none',
                  }),
                ],
              }}
            >
              <PageRow miscStyles={{ marginBottom: '0', }}>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.stripbottom'], adUnitPathTemplate)} />
              </PageRow>
            </FelaComponent>
          </Fragment>
        </GetComponentProvider>
      )}
    </FelaTheme>
  );
}

export default (props: Props) => (<FinanceDataGetter><MainLayout {...props} /></FinanceDataGetter>);
